import { useState, useContext } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';

import {
  SimpleGrid,
  Box,
  Collapse,
  Button,
  Icon,
  Stack,
  Radio,
  RadioGroup,
  useColorModeValue,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import FormInput from 'components/fields/auto/FormInput';
import DateInput from 'components/fields/auto/DateInput';
import RadioInput from 'components/fields/auto/RadioInput';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';
import getDateString from 'helpers/getDateString';
import getFileType092 from 'helpers/getFileType092';

import getValidationMessage from 'helpers/getValidationMessage';
import Message from 'components/fields/auto/Message';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { changeVehicle } from 'reducers/autoPersonalFormReducer';

import {
  Validation006Resolver,
  Validation124Resolver,
  Validation153aResolver,
  Validation153bResolver,
} from 'components/fields/auto/resolvers';

import type { APIPersonalAutoApplicationFormVehicle } from 'types/api.types';

import {
  FILE_TYPE,
  PROVINCE_ALBERTA,
  PROVINCE_NEW_BRUNSWICK,
  PROVINCE_NOVA_SCOTIA,
  isAtlanticProvince,
  isNonOntarioProvince,
} from 'variables/constants';
import { SECTIONS } from 'views/auto_form/constants';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import RatingInformation from 'views/auto_form/sections/Vehicles/components/RatingInformation';
import Coverages from 'views/auto_form/sections/Vehicles/components/Coverages';
import Lessor from 'views/auto_form/sections/Vehicles/components/Lessor';
import LienHolder from 'views/auto_form/sections/Vehicles/components/LienHolder';

import getVehicleSummary from 'views/auto_form/sections/Vehicles/helpers/getVehicleSummary';
import ConditionalDetails from 'views/auto_form/components/ConditionalDetails';

const Vehicle = ({
  vehicle,
  index,
}: {
  vehicle: APIPersonalAutoApplicationFormVehicle;
  index: number;
}) => {
  const { validations } = useContext(ApplicationFormContext);
  const isValidationResolversEnabled = useFeatureFlag(
    'enable-validation-resolvers',
  );
  const isValidation124ResolverEnabled = useFeatureFlag(
    'enable-validation-124-resolver',
  );
  const isBaraEnabled = useFeatureFlag('enable-bara');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useAppDispatch();
  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isAlberta = province === PROVINCE_ALBERTA;
  const isAtlantic = isAtlanticProvince(province);
  const isNovaScotia = province === PROVINCE_NOVA_SCOTIA;
  const isNewBrunswick = province === PROVINCE_NEW_BRUNSWICK;
  const isNonOntario = isNonOntarioProvince(province);

  const billOfSaleEndorsement = isNovaScotia
    ? '(N)SEF 43R/43R(L)'
    : isNewBrunswick
      ? 'NBEF 43R/43R(L)'
      : isAtlantic
        ? 'SCF'
        : 'OPCF43';
  const driversCount = useAppSelector(
    (state) => state.autoPersonalForm.data?.drivers?.length || 0,
  );
  const vehiclesCount = useAppSelector(
    (state) => state.autoPersonalForm.data?.vehicles?.length || 0,
  );
  const hasOccasionalDrivers = driversCount > vehiclesCount;

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('', 'gray.800');
  const shadowColor = useColorModeValue(
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 2px 4px rgba(0,0,0,0.3)',
  );

  const validation131 = getValidationMessage(validations, '131', index + 1);
  const validation097 = getValidationMessage(validations, '097', index + 1);
  const validation110 = getValidationMessage(validations, '110', index + 1);
  const validation118 = getValidationMessage(validations, '118', index + 1);
  const validation149 = getValidationMessage(validations, '149', index + 1);
  const validation150 = getValidationMessage(validations, '150', index + 1);
  const validation151 = getValidationMessage(validations, '151', index + 1);
  const validation121 = getValidationMessage(validations, '121', index + 1);
  const validation121Name =
    validations.find(
      (validation: Validation) => validation.validation_id === '121',
    )?.validation_name || 'Intact - Photos';
  const validation127 = getValidationMessage(validations, '127', index + 1);
  const validation132 = getValidationMessage(validations, '132', index + 1);
  const validation134 = getValidationMessage(validations, '134', index + 1);
  const validation037 = getValidationMessage(validations, '037', index + 1);
  const validation043 = getValidationMessage(validations, '043', index + 1);
  const validation043a = getValidationMessage(validations, '043a', index + 1);
  const validation043b = getValidationMessage(validations, '043b', index + 1);
  const validation070 = getValidationMessage(validations, '070', index + 1);
  const validation111 = getValidationMessage(validations, '111', index + 1);
  const validation124 = getValidationMessage(validations, '124', index + 1);
  const validation135 = getValidationMessage(validations, '135', index + 1);
  const validation148 = getValidationMessage(validations, '148', index + 1);
  const validation153b = getValidationMessage(validations, '153b', index + 1);
  const validation153a = getValidationMessage(validations, '153a', index + 1);

  const fileType092_110 = getFileType092(validations);
  const validation147 = getValidationMessage(validations, '147', index + 1);

  const onVehicleChange = (event: any) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(changeVehicle({ name, index, value: payload }));
  };

  const onDateChange = (date: Date, name: string) => {
    dispatch(changeVehicle({ name, index, value: getDateString(date) }));
  };

  const getOwnershipValue = (
    vehicle: APIPersonalAutoApplicationFormVehicle,
  ) => {
    if (vehicle.is_owend) return 'owned';
    if (vehicle.is_leased) return 'leased';
    return null;
  };

  const onOwnershipToggle = (value: string) => {
    const isOwned = value === 'owned';
    dispatch(
      changeVehicle({
        name: 'is_owend',
        index,
        value: isOwned,
      }),
    );
    dispatch(
      changeVehicle({
        name: 'is_leased',
        index,
        value: !isOwned,
      }),
    );
  };

  const getPurchaseConditionValue = (
    vehicle: APIPersonalAutoApplicationFormVehicle,
  ) => {
    if (vehicle.is_purchased_new) return 'new';
    if (vehicle.is_purchased_used) return 'used';
    return null;
  };

  const onPurchaseConditionToggle = (value: string) => {
    const isNew = value === 'new';

    dispatch(
      changeVehicle({
        name: 'is_purchased_new',
        index,
        value: isNew,
      }),
    );

    dispatch(
      changeVehicle({
        name: 'is_purchased_used',
        index,
        value: !isNew,
      }),
    );
  };

  const getFuelTypeValue = (vehicle: APIPersonalAutoApplicationFormVehicle) => {
    if (vehicle._info?.is_non_private_passenger) {
      if (vehicle.other_fuel_type === 'Electric') return 'electric';
      if (vehicle.is_gas) return 'gas';
      if (vehicle.is_diesel) return 'diesel';
      return null;
    }
    if (vehicle.other_fuel_type) return 'other';
    if (vehicle.is_gas) return 'gas';
    if (vehicle.is_diesel) return 'diesel';
    return null;
  };

  const onFuelTypeToggle = (value: string) => {
    const isGas = value === 'gas';
    const isDiesel = value === 'diesel';
    const isElectric = value === 'electric';

    dispatch(
      changeVehicle({
        name: 'is_gas',
        index,
        value: isGas,
      }),
    );
    dispatch(
      changeVehicle({
        name: 'is_diesel',
        index,
        value: isDiesel,
      }),
    );
    dispatch(
      changeVehicle({
        name: 'other_fuel_type',
        index,
        value: isElectric ? 'Electric' : '',
      }),
    );
  };

  const getWinterTireValidationId = () => {
    if (validation151) return '151';
    if (validation150) return '150';
    if (validation118) return '118';
    return '076';
  };

  const getWinterTireFileType = () => {
    if (validation151) return undefined;
    if (validation150) return FILE_TYPE.TYPE_150;
    if (validation118) return FILE_TYPE.TYPE_118;
    return FILE_TYPE.TYPE_076;
  };

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      bg={bgColor}
      boxShadow={shadowColor}
      overflow="hidden"
      mb="20px"
    >
      <Box
        rounded="5px"
        p="10px"
        backgroundColor="brand.400"
        color="white"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box fontWeight="medium">{getVehicleSummary(vehicle, index)}</Box>
        <Button
          variant="ghost"
          size="sm"
          color="white"
          _hover={{ bg: 'whiteAlpha.200' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </Button>
      </Box>

      <Collapse in={!isCollapsed} animateOpacity>
        <Box p="6">
          <SimpleGrid columns={6} spacing="20px" mb="6">
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Model Year"
              name="model_year"
              value={vehicle.model_year}
              onChange={onVehicleChange}
              required
              disabled
            />

            <FormInput
              section={SECTIONS.VEHICLES}
              label="Make or Trade Name"
              name="make"
              value={vehicle.make}
              onChange={onVehicleChange}
              required
              disabled
            />

            <FormInput
              section={SECTIONS.VEHICLES}
              label="Model"
              name="model"
              value={vehicle.model}
              onChange={onVehicleChange}
              required={vehicle._info.is_model_mandatory}
              disabled
            />

            <FormInput
              section={SECTIONS.VEHICLES}
              label="Body Type"
              name="body_type"
              value={vehicle.body_type}
              onChange={onVehicleChange}
              disabled
            />

            <FormInput
              section={SECTIONS.VEHICLES}
              label="Engine size"
              name="no_of_cylindersor_engine_size"
              value={vehicle.no_of_cylindersor_engine_size}
              onChange={onVehicleChange}
              disabled
              infoText="No. of Cylinders or Engine Size"
            />

            <FormInput
              section={SECTIONS.VEHICLES}
              label="Weight"
              name="weight"
              value={vehicle.weight}
              onChange={onVehicleChange}
              disabled={isNonOntario}
              infoText="Gross Vehicle Weight Rating (Kg)"
            />
          </SimpleGrid>

          <SimpleGrid columns={6} spacing="20px" mb="6">
            <FormInput
              section={SECTIONS.VEHICLES}
              label="VIN"
              name="vin"
              value={vehicle.vin}
              onChange={onVehicleChange}
              infoText="Vehicle Identification No. (Serial No.)"
              required
              disabled
            />
            <DateInput
              section={SECTIONS.VEHICLES}
              label="Purchase Date"
              date={getDateFromString(vehicle.purchased_date)}
              onChange={(date: Date) => onDateChange(date, 'purchased_date')}
              message={{
                validationId: '040',
                storageKey: 'vehicle',
                index: index + 1,
                file: FILE_TYPE.TYPE_040,
                hideRemarks: true,
                compact: true,
              }}
              disabled
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label={
                isNovaScotia || isNewBrunswick
                  ? 'Purchase Price Including Equipment'
                  : isAlberta
                    ? 'Purchase Price Including Accessories Equipment'
                    : 'Purchase Price'
              }
              name="purchase_price"
              value={vehicle.purchase_price}
              onChange={onVehicleChange}
              disabled
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Annual Km"
              name="estimated_annual_driving_distance"
              value={vehicle.estimated_annual_driving_distance}
              onChange={onVehicleChange}
              message={{
                validationId: '014',
                storageKey: 'vehicle',
                index: index + 1,
                compact: true,
              }}
              infoText="Annual Driving Distance"
              disabled
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Commute One - Way"
              name="commute_one_way"
              value={vehicle.commute_one_way}
              onChange={onVehicleChange}
              message={{
                validationId: '012',
                storageKey: 'vehicle',
                index: index + 1,
                compact: true,
              }}
              disabled
            />
            <FormInput
              section={SECTIONS.VEHICLES}
              label="Business Use %"
              name="business_use_percentage"
              value={vehicle.business_use_percentage}
              onChange={onVehicleChange}
              message={{
                validationId: '013',
                storageKey: 'vehicle',
                index: index + 1,
                compact: true,
              }}
              disabled
            />
          </SimpleGrid>

          <SimpleGrid columns={6} spacing="20px" mb="6">
            <RadioInput
              label="Pleasure use"
              subLabel={`Vehicle ${index + 1}`}
              section={SECTIONS.VEHICLES}
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(vehicle.is_pleasure)}
              onChange={(value: string) => {
                onVehicleChange({
                  target: {
                    name: 'is_pleasure',
                    value: value === 'true',
                  },
                });
              }}
              required
              stacked
              disabled
            />
            <RadioInput
              label="Purchase condition"
              subLabel={`Vehicle ${index + 1}`}
              section={SECTIONS.VEHICLES}
              options={[
                { value: 'new', label: 'New' },
                { value: 'used', label: 'Used' },
              ]}
              value={getPurchaseConditionValue(vehicle)}
              onChange={(value: string) => onPurchaseConditionToggle(value)}
              required={vehicle._info.is_purchase_condition_mandatory}
              stacked
              disabled={
                isNonOntario || !vehicle._info.is_purchase_condition_mandatory
              }
            />
            {(vehicle.is_purchased_new ||
              vehicle._info.is_demo_vehicle_disabled) && (
              <RadioInput
                label="Demo Vehicle?"
                subLabel={`Vehicle ${index + 1}`}
                section={SECTIONS.VEHICLES}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                value={getRadioYesNo(vehicle.is_demo_vehicle)}
                onChange={(value: string) =>
                  onVehicleChange({
                    target: {
                      name: 'is_demo_vehicle',
                      value: value === 'true',
                    },
                  })
                }
                message={{
                  validationId: 'demo_vehicle_odometer_reading',
                  storageKey: 'vehicle',
                  index: index + 1,
                  manual: {
                    message: 'Please provide odometer reading',
                    show: vehicle.is_demo_vehicle,
                  },
                  compact: true,
                }}
                required
                stacked
                disabled={
                  isNonOntario || vehicle._info.is_demo_vehicle_disabled
                }
              />
            )}
            <RadioInput
              label={isNonOntario ? 'Registration' : 'Ownership'}
              subLabel={`Vehicle ${index + 1}`}
              section={SECTIONS.VEHICLES}
              options={[
                { value: 'owned', label: 'Owned' },
                { value: 'leased', label: 'Leased' },
              ]}
              value={getOwnershipValue(vehicle)}
              onChange={(value: string) => onOwnershipToggle(value)}
              required
              stacked
              disabled
            />
            <RadioInput
              label="Fuel type"
              subLabel={`Vehicle ${index + 1}`}
              section={SECTIONS.VEHICLES}
              options={
                vehicle._info?.is_non_private_passenger
                  ? [
                      { value: 'gas', label: 'Gas' },
                      { value: 'diesel', label: 'Diesel' },
                      { value: 'electric', label: 'Electric' },
                    ]
                  : !vehicle.other_fuel_type
                    ? [
                        { value: 'gas', label: 'Gas' },
                        { value: 'diesel', label: 'Diesel' },
                      ]
                    : [{ value: 'other', label: vehicle.other_fuel_type }]
              }
              value={getFuelTypeValue(vehicle)}
              onChange={(value: string) => onFuelTypeToggle(value)}
              message={{
                validationId: '016',
                storageKey: 'vehicle',
                index: index + 1,
                manual: {
                  message:
                    'Please provide details about fuel type other than gas or diesel',
                  show:
                    !!vehicle.other_fuel_type &&
                    !vehicle._info?.is_non_private_passenger,
                },
                compact: true,
              }}
              stacked
              disabled={
                isNonOntario || !vehicle._info?.is_non_private_passenger
              }
              required={!vehicle._info?.is_non_private_passenger}
            />
            <RadioInput
              label="Has winter tires?"
              subLabel={`Vehicle ${index + 1}`}
              section={SECTIONS.VEHICLES}
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              value={getRadioYesNo(vehicle.is_winter_tires)}
              onChange={(value: string) => {
                onVehicleChange({
                  target: {
                    name: 'is_winter_tires',
                    value: value === 'true',
                  },
                });
              }}
              message={{
                validationId: getWinterTireValidationId(),
                storageKey: 'vehicle',
                index: index + 1,
                file: getWinterTireFileType(),
                compact: true,
                hideRemarks: true,
              }}
              required
              stacked
              disabled
            />
          </SimpleGrid>

          <SimpleGrid columns={6} spacing="20px">
            <ConditionalDetails
              title="Commercial use details"
              isOpen={isNonOntario && vehicle.is_commercial === true}
              question={
                <Box>
                  <RadioInput
                    label="Commercial use"
                    subLabel={`Vehicle ${index + 1}`}
                    section={SECTIONS.VEHICLES}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                    value={getRadioYesNo(vehicle.is_commercial)}
                    onChange={(value: string) => {
                      onVehicleChange({
                        target: {
                          name: 'is_commercial',
                          value: value === 'true',
                        },
                      });
                    }}
                    required
                    stacked
                  />
                  {isNonOntario && vehicle.is_commercial === true && (
                    <Alert
                      status="error"
                      mt="10px"
                      borderRadius="md"
                      fontSize="sm"
                    >
                      <AlertIcon />
                      <AlertDescription>
                        This vehicle is marked for commercial use. Please refer
                        to BIG Portfolio Underwriting
                        {isBaraEnabled
                          ? ' or submit an After-Hours Referral (MP/Delegate)'
                          : ''}
                        .
                      </AlertDescription>
                    </Alert>
                  )}
                </Box>
              }
            >
              <RadioInput
                label="Operating radius > 40 km?"
                subLabel={`Vehicle ${index + 1}`}
                section={SECTIONS.VEHICLES}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                value={getRadioYesNo(vehicle.is_operating_radius_over_40km)}
                onChange={(value: string) => {
                  onVehicleChange({
                    target: {
                      name: 'is_operating_radius_over_40km',
                      value: value === 'true',
                    },
                  });
                }}
                required
                stacked
              />
              <FormInput
                section={SECTIONS.VEHICLES}
                label="Describe the complete use of the vehicle"
                name="commercial_use_description"
                value={vehicle.commercial_use_description || ''}
                onChange={onVehicleChange}
                subLabel={`Vehicle ${index + 1}`}
                required
              />
              <FormInput
                section={SECTIONS.VEHICLES}
                label="Merchandise and materials carried (%)"
                name="merchandise_materials_percentage"
                value={vehicle.merchandise_materials_percentage || ''}
                onChange={onVehicleChange}
                subLabel={`Vehicle ${index + 1}`}
                required
              />
            </ConditionalDetails>
            {isNewBrunswick && (
              <Box>
                <RadioInput
                  label="First Chance Discount?"
                  subLabel={`Vehicle ${index + 1}`}
                  section={SECTIONS.VEHICLES}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={getRadioYesNo(vehicle.is_first_chance_discount)}
                  onChange={(value: string) => {
                    onVehicleChange({
                      target: {
                        name: 'is_first_chance_discount',
                        value: value === 'true',
                      },
                    });
                  }}
                  required
                  stacked
                />
              </Box>
            )}
            <Box>
              <RadioInput
                label="Farm use"
                subLabel={`Vehicle ${index + 1}`}
                section={SECTIONS.VEHICLES}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                value={getRadioYesNo(vehicle.is_farm)}
                onChange={(value: string) => {
                  onVehicleChange({
                    target: {
                      name: 'is_farm',
                      value: value === 'true',
                    },
                  });
                }}
                required
                stacked
              />
              {isNonOntario && vehicle.is_farm === true && (
                <Alert status="error" mt="10px" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>
                    This vehicle is marked for farm use. Please refer to BIG
                    Portfolio Underwriting
                    {isBaraEnabled
                      ? ' or submit an After-Hours Referral (MP/Delegate)'
                      : ''}
                    .
                  </AlertDescription>
                </Alert>
              )}
            </Box>
            <ConditionalDetails
              title="Carpool / ride share details"
              columns={2}
              isOpen={
                isNonOntario &&
                (vehicle.is_car_pooling === true ||
                  (!isAlberta && vehicle.is_ride_sharing === true))
              }
              question={
                <RadioInput
                  label={
                    isNewBrunswick || isNovaScotia
                      ? 'Is the vehicle used in carpools or other share-the-ride agreements?'
                      : isAlberta
                        ? 'Is used in car pools or share-the-ride arrangements?'
                        : 'Is used for car pooling?'
                  }
                  subLabel={`Vehicle ${index + 1}`}
                  section={SECTIONS.VEHICLES}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={getRadioYesNo(vehicle.is_car_pooling)}
                  onChange={(value: string) => {
                    onVehicleChange({
                      target: {
                        name: 'is_car_pooling',
                        value: value === 'true',
                      },
                    });
                  }}
                  message={
                    isNewBrunswick || isNovaScotia
                      ? undefined
                      : {
                          validationId: 'car_pooling',
                          storageKey: 'vehicle',
                          index: index + 1,
                          manual: {
                            message: 'Please provide car pooling details',
                            show: vehicle.is_car_pooling,
                          },
                          compact: true,
                        }
                  }
                  required
                  stacked
                />
              }
            >
              <FormInput
                section={SECTIONS.VEHICLES}
                label="Number of Passengers"
                name="number_of_passengers"
                value={vehicle.number_of_passengers || ''}
                onChange={onVehicleChange}
                subLabel={`Vehicle ${index + 1}`}
                required
              />
              {isAlberta && (
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Frequency (# of times per period)"
                  name="carpool_frequency"
                  value={vehicle.carpool_frequency || ''}
                  onChange={onVehicleChange}
                  subLabel={`Vehicle ${index + 1}`}
                  required
                />
              )}
            </ConditionalDetails>

            {isNonOntario && (
              <>
                <ConditionalDetails
                  title="Special equipment / custom paint details"
                  columns={2}
                  isOpen={vehicle.is_special_equipment === true}
                  question={
                    <RadioInput
                      label="Is there special equipment and/or custom paint finish?"
                      subLabel={`Vehicle ${index + 1}`}
                      section={SECTIONS.VEHICLES}
                      options={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]}
                      value={getRadioYesNo(vehicle.is_special_equipment)}
                      onChange={(value: string) => {
                        onVehicleChange({
                          target: {
                            name: 'is_special_equipment',
                            value: value === 'true',
                          },
                        });
                      }}
                      required
                      stacked
                    />
                  }
                >
                  <FormInput
                    section={SECTIONS.VEHICLES}
                    label="Description"
                    name="special_equipment_description"
                    value={vehicle.special_equipment_description || ''}
                    onChange={onVehicleChange}
                    subLabel={`Vehicle ${index + 1}`}
                    required
                  />
                  <FormInput
                    section={SECTIONS.VEHICLES}
                    label="Added value"
                    name="special_equipment_added_value"
                    value={vehicle.special_equipment_added_value || ''}
                    onChange={onVehicleChange}
                    subLabel={`Vehicle ${index + 1}`}
                    required
                  />
                </ConditionalDetails>

                <ConditionalDetails
                  title="Transportation of goods details"
                  columns={2}
                  isOpen={vehicle.is_goods_for_reward === true}
                  question={
                    <RadioInput
                      label={
                        isNovaScotia || isNewBrunswick
                          ? 'Will the automobile be used for the transportation of goods for compensation?'
                          : 'Will the automobile be used for the transportation of goods (examples: freight, food, parcels) for reward, even on an occasional basis?'
                      }
                      subLabel={`Vehicle ${index + 1}`}
                      section={SECTIONS.VEHICLES}
                      options={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]}
                      value={getRadioYesNo(vehicle.is_goods_for_reward)}
                      onChange={(value: string) => {
                        onVehicleChange({
                          target: {
                            name: 'is_goods_for_reward',
                            value: value === 'true',
                          },
                        });
                      }}
                      required
                      stacked
                    />
                  }
                >
                  <FormInput
                    section={SECTIONS.VEHICLES}
                    label="Class of licence or certificate"
                    name="goods_licence_class"
                    value={vehicle.goods_licence_class || ''}
                    onChange={onVehicleChange}
                    subLabel={`Vehicle ${index + 1}`}
                    required
                  />
                  <FormInput
                    section={SECTIONS.VEHICLES}
                    label="Radius of operations"
                    name="goods_radius"
                    value={vehicle.goods_radius || ''}
                    onChange={onVehicleChange}
                    subLabel={`Vehicle ${index + 1}`}
                    required
                  />
                </ConditionalDetails>
              </>
            )}

            {(isNewBrunswick || isNovaScotia) && (
              <RadioInput
                label="Has vehicle been modified, altered, or customized or is there any unrepaired damage including damage to glass?"
                subLabel={`Vehicle ${index + 1}`}
                section={SECTIONS.VEHICLES}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                value={getRadioYesNo(vehicle.is_modified_customized)}
                onChange={(value: string) => {
                  onVehicleChange({
                    target: {
                      name: 'is_modified_customized',
                      value: value === 'true',
                    },
                  });
                  onVehicleChange({
                    target: {
                      name: 'is_unrepaired_damage',
                      value: value === 'true',
                    },
                  });
                }}
                message={{
                  validationId: 'modified_customized',
                  storageKey: 'vehicle',
                  index: index + 1,
                  manual: {
                    message:
                      'Please provide details about the modification, alteration, customization or unrepaired damage',
                    show: vehicle.is_modified_customized,
                  },
                  file: FILE_TYPE.TYPE_MODIFED_CUSTOMIZED,
                  compact: true,
                }}
                required
                stacked
              />
            )}

            {!isNewBrunswick && !isNovaScotia && (
              <>
                <Stack direction="column" spacing={2} align="flex-start">
                  <RadioInput
                    label={
                      isAlberta
                        ? 'Unrepaired Damage Including Damage to Glass?'
                        : 'Unrepaired Damage?'
                    }
                    subLabel={`Vehicle ${index + 1}`}
                    section={SECTIONS.VEHICLES}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                    value={getRadioYesNo(vehicle.is_unrepaired_damage)}
                    onChange={(value: string) => {
                      onVehicleChange({
                        target: {
                          name: 'is_unrepaired_damage',
                          value: value === 'true',
                        },
                      });
                    }}
                    message={
                      isValidationResolversEnabled &&
                      vehicle.is_unrepaired_damage
                        ? undefined
                        : {
                            validationId: 'unrepaired_damage',
                            storageKey: 'vehicle',
                            index: index + 1,
                            manual: {
                              message:
                                'Please provide details about unrepaired damage',
                              show: vehicle.is_unrepaired_damage,
                            },
                            file: FILE_TYPE.TYPE_UNREPAIRED_DAMAGE,
                            compact: true,
                          }
                    }
                    required
                    stacked
                  />
                  {isValidationResolversEnabled &&
                    vehicle.is_unrepaired_damage && (
                      <Box mt="10px">
                        <Validation006Resolver
                          section={SECTIONS.VEHICLES}
                          itemKey={`vehicle-${index + 1}`}
                          validationId="017"
                          marketApprovalFileType={
                            FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type
                          }
                          forceShow
                          customAlertMessage="Please provide details about unrepaired damage"
                          compact
                        />
                      </Box>
                    )}
                </Stack>
                <RadioInput
                  label={
                    isAlberta
                      ? 'Are there any modifications or customization, other than repairs or restorations that affect the original manufacturer’s design specifications or increase the value of the automobile?'
                      : 'Modified / Customized'
                  }
                  subLabel={`Vehicle ${index + 1}`}
                  section={SECTIONS.VEHICLES}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={getRadioYesNo(vehicle.is_modified_customized)}
                  onChange={(value: string) => {
                    onVehicleChange({
                      target: {
                        name: 'is_modified_customized',
                        value: value === 'true',
                      },
                    });
                  }}
                  message={{
                    validationId: 'modified_customized',
                    storageKey: 'vehicle',
                    index: index + 1,
                    manual: {
                      message:
                        'Please provide details about modification and customization',
                      show: vehicle.is_modified_customized,
                    },
                    file: FILE_TYPE.TYPE_MODIFED_CUSTOMIZED,
                    compact: true,
                  }}
                  required
                  stacked
                />
              </>
            )}

            {!isNewBrunswick && !isNovaScotia && !isAlberta && (
              <RadioInput
                label="Is used for ride sharing?"
                subLabel={`Vehicle ${index + 1}`}
                section={SECTIONS.VEHICLES}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                value={getRadioYesNo(vehicle.is_ride_sharing)}
                onChange={(value: string) => {
                  onVehicleChange({
                    target: {
                      name: 'is_ride_sharing',
                      value: value === 'true',
                    },
                  });
                }}
                message={{
                  validationId: 'ride_sharing',
                  storageKey: 'vehicle',
                  index: index + 1,
                  manual: {
                    message: 'Please provide details about ride sharing',
                    show: vehicle.is_ride_sharing,
                  },
                  compact: true,
                }}
                required
                stacked
              />
            )}

            {isAlberta && (
              <ConditionalDetails
                title="Use outside Canada details"
                columns={2}
                isOpen={vehicle.is_used_outside_canada === true}
                question={
                  <RadioInput
                    label="Used outside Canada?"
                    subLabel={`Vehicle ${index + 1}`}
                    section={SECTIONS.VEHICLES}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                    value={getRadioYesNo(vehicle.is_used_outside_canada)}
                    onChange={(value: string) => {
                      onVehicleChange({
                        target: {
                          name: 'is_used_outside_canada',
                          value: value === 'true',
                        },
                      });
                    }}
                    message={{
                      validationId: 'used_outside_canada',
                      storageKey: 'vehicle',
                      index: index + 1,
                      manual: {
                        message:
                          'Please provide details about vehicle use outside Canada',
                        show: vehicle.is_used_outside_canada === true,
                      },
                      compact: true,
                    }}
                    required
                    stacked
                  />
                }
              >
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Number of months used outside Canada"
                  name="used_outside_canada_months"
                  value={vehicle.used_outside_canada_months || ''}
                  onChange={onVehicleChange}
                  subLabel={`Vehicle ${index + 1}`}
                  required
                />
              </ConditionalDetails>
            )}

            {isAlberta && (
              <ConditionalDetails
                title="Anti-theft device details"
                isOpen={vehicle.is_anti_theft_device === true}
                question={
                  <RadioInput
                    label="Anti-Theft Device?"
                    subLabel={`Vehicle ${index + 1}`}
                    section={SECTIONS.VEHICLES}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                    value={getRadioYesNo(vehicle.is_anti_theft_device)}
                    onChange={(value: string) => {
                      onVehicleChange({
                        target: {
                          name: 'is_anti_theft_device',
                          value: value === 'true',
                        },
                      });
                    }}
                    required
                    stacked
                  />
                }
              >
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Device type"
                  name="anti_theft_device_type"
                  value={vehicle.anti_theft_device_type || ''}
                  onChange={onVehicleChange}
                  subLabel={`Vehicle ${index + 1}`}
                  required
                />
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Device characteristics"
                  name="anti_theft_characteristics"
                  value={vehicle.anti_theft_characteristics || ''}
                  onChange={onVehicleChange}
                  subLabel={`Vehicle ${index + 1}`}
                  required
                />
                <FormInput
                  section={SECTIONS.VEHICLES}
                  label="Product code"
                  name="anti_theft_product_code"
                  value={vehicle.anti_theft_product_code || ''}
                  onChange={onVehicleChange}
                  subLabel={`Vehicle ${index + 1}`}
                  required
                />
              </ConditionalDetails>
            )}

            {(isAlberta || isNewBrunswick || isNovaScotia) && (
              <FormInput
                section={SECTIONS.VEHICLES}
                label="Occasional Driver #"
                name="occasional_driver_assigned"
                value={vehicle.occasional_driver_assigned}
                onChange={onVehicleChange}
                disabled={
                  (isNonOntario && !isNewBrunswick && !isNovaScotia) ||
                  !hasOccasionalDrivers
                }
                required={hasOccasionalDrivers}
                infoText="Driver number of occasional driver assigned to this vehicle"
              />
            )}

            {validation131 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Commute Distance
                </FormLabel>
                <Box pb="5px">Commute Distance Under 10km</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="131"
                  storageKey="vehicle"
                  index={index + 1}
                  compact
                />
              </FormControl>
            )}

            {validation097 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Mandatory {isNonOntario ? 'Registration' : 'Ownership'}
                </FormLabel>
                <Box pb="5px">Missing from AutoPlus</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="097"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_097}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation110 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Echelon - Photos
                </FormLabel>
                <Box pb="5px">Vehicle photos</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="110"
                  storageKey="vehicle"
                  index={index + 1}
                  file={fileType092_110}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation121 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  {validation121Name}
                </FormLabel>
                <Box pb="5px">Vehicle photos</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="121"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_121}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation153b && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Vehicle Age (15+ years)
                </FormLabel>
                <Box pb="5px">Vehicle photos & Safety Certificate</Box>
                <Validation153bResolver
                  section={SECTIONS.VEHICLES}
                  itemKey={`vehicle-${index + 1}`}
                  failedObjectId={index + 1}
                  vehicleLabel={`${vehicle.model_year} ${vehicle.make} ${vehicle.model} (VIN: ${vehicle.vin})`}
                  compact
                />
              </FormControl>
            )}

            {validation153a && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Unica - Vehicle Age (15+ years)
                </FormLabel>
                <Box pb="5px">
                  Time-stamped vehicle photos &amp; Safety Certificate
                </Box>
                <Validation153aResolver
                  section={SECTIONS.VEHICLES}
                  itemKey={`vehicle-${index + 1}`}
                  failedObjectId={index + 1}
                  vehicleLabel={`${vehicle.model_year} ${vehicle.make} ${vehicle.model} (VIN: ${vehicle.vin})`}
                  compact
                />
              </FormControl>
            )}

            {validation127 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Gore - OPCF 43
                </FormLabel>
                <Box pb="5px">Continuous coverage</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="127"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_127_132}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation149 && !isAlberta && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Winter Tire Discount
                </FormLabel>
                <Box pb="5px">Disclosure statement and signature</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="149"
                  storageKey="vehicle"
                  index={index + 1}
                  compact
                  renderRemarkInput={({ remark, onUpdateRemark }) => (
                    <RadioGroup
                      value={remark.text ? 'yes' : 'no'}
                      onChange={(value) => {
                        const start = validation149.indexOf('I understand');
                        const raw =
                          start !== -1
                            ? validation149.slice(start)
                            : validation149;
                        const declarationText = raw.endsWith('"')
                          ? raw.slice(0, -1)
                          : raw;
                        onUpdateRemark({
                          text: value === 'yes' ? declarationText : '',
                          status: value === 'yes' ? 'approved' : 'created',
                        });
                      }}
                    >
                      <Stack direction="row" spacing={4}>
                        <Radio value="yes">I acknowledge the declaration</Radio>
                        <Radio value="no">No</Radio>
                      </Stack>
                    </RadioGroup>
                  )}
                />
              </FormControl>
            )}

            {validation132 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Wawanesa - OPCF 43/43A
                </FormLabel>
                <Box pb="5px">Coverage</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="132"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_127_132}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation134 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  {billOfSaleEndorsement} - Bill of Sale/Lease Agreement
                  Required & Proof of Prior
                </FormLabel>
                <Box pb="5px">Proof of prior coverage</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="134"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_127_132}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation037 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  DCPD waiver
                </FormLabel>
                <Box pb="5px">OPCF 49 endorsement</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="037"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_037}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation043 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Excluded driver
                </FormLabel>
                <Box pb="5px">Check for OPCF 28a</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="043"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_043}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation043a && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Excluded driver
                </FormLabel>
                <Box pb="5px">Check for OPCF 28a</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="043a"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_043a}
                  compact
                  hideRemarks
                />
              </FormControl>
            )}

            {validation043b && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Excluded driver
                </FormLabel>
                <Box pb="5px">Check for OPCF 28a</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="043b"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_043b}
                  compact
                  hideRemarks
                  modalExtraContent={
                    <Validation006Resolver
                      section={SECTIONS.VEHICLES}
                      itemKey={`vehicle-${index + 1}-043b-market`}
                      validationId="043b"
                      marketApprovalFileType={
                        FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type
                      }
                      forceShow
                      emailOnly
                      hideAlertMessage
                      validationTypeKey="remarks_market_approval"
                    />
                  }
                />
              </FormControl>
            )}

            {validation043b && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Excluded driver
                </FormLabel>
                <Box pb="5px">Check for OPCF 28a</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="043b"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_043b}
                  compact
                  hideRemarks
                  modalExtraContent={
                    <Validation006Resolver
                      section={SECTIONS.VEHICLES}
                      itemKey={`vehicle-${index + 1}-043b-market`}
                      validationId="043b"
                      marketApprovalFileType={
                        FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type
                      }
                      forceShow
                      emailOnly
                      hideAlertMessage
                      validationTypeKey="remarks_market_approval"
                    />
                  }
                />
              </FormControl>
            )}

            {validation070 &&
              (isValidationResolversEnabled ? (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Vehicle value
                  </FormLabel>
                  <Box pb="5px">Portage</Box>
                  <Validation006Resolver
                    section={SECTIONS.VEHICLES}
                    itemKey={`vehicle-${index + 1}`}
                    validationId="070"
                    compact
                  />
                </FormControl>
              ) : (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Vehicle value
                  </FormLabel>
                  <Box pb="5px">Portage</Box>
                  <Message
                    section={SECTIONS.VEHICLES}
                    validationId="070"
                    storageKey="vehicle"
                    index={index + 1}
                    file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
                    compact
                  />
                </FormControl>
              ))}

            {validation111 &&
              (isValidationResolversEnabled ? (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Registered in Business Name
                  </FormLabel>
                  <Box pb="5px">Market approval required</Box>
                  <Validation006Resolver
                    section={SECTIONS.VEHICLES}
                    itemKey={`vehicle-${index + 1}`}
                    validationId="111"
                    allowPhoneApproval
                    compact
                  />
                </FormControl>
              ) : (
                <FormControl isRequired>
                  <FormLabel
                    display="flex"
                    fontSize="sm"
                    fontWeight="bold"
                    _hover={{ cursor: 'pointer' }}
                    pb="10px"
                    m="0"
                  >
                    Registered in Business Name
                  </FormLabel>
                  <Box pb="5px">Market approval required</Box>
                  <Message
                    section={SECTIONS.VEHICLES}
                    validationId="111"
                    storageKey="vehicle"
                    index={index + 1}
                    file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
                    compact
                  />
                </FormControl>
              ))}

            {validation124 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Vehicle Purchase Date
                </FormLabel>
                <Box pb="5px">Does not match</Box>
                {isValidation124ResolverEnabled ? (
                  <Validation124Resolver
                    section={SECTIONS.VEHICLES}
                    itemKey={`vehicle-${index + 1}`}
                    compact
                  />
                ) : (
                  <Message
                    section={SECTIONS.VEHICLES}
                    validationId="124"
                    storageKey="vehicle"
                    index={index + 1}
                    file={FILE_TYPE.TYPE_124}
                    compact
                  />
                )}
              </FormControl>
            )}

            {validation135 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Bill of Sale or Lease Agreement
                </FormLabel>
                <Box pb="5px">
                  {isAlberta
                    ? 'SEF 43R/SEF 43R(L)'
                    : isAtlantic
                      ? billOfSaleEndorsement
                      : 'OPCF43/43A'}
                </Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="135"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_135}
                  hideRemarks
                  compact
                />
              </FormControl>
            )}

            {validation148 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Vehicle Inspection Report
                </FormLabel>
                <Box pb="5px">Pembridge</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="148"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_148}
                  hideRemarks
                  compact
                />
              </FormControl>
            )}

            {validation147 && (
              <FormControl isRequired>
                <FormLabel
                  display="flex"
                  fontSize="sm"
                  fontWeight="bold"
                  _hover={{ cursor: 'pointer' }}
                  pb="10px"
                  m="0"
                >
                  Theft Recovery Device Discount
                </FormLabel>
                <Box pb="5px">Installation certificate</Box>
                <Message
                  section={SECTIONS.VEHICLES}
                  validationId="147"
                  storageKey="vehicle"
                  index={index + 1}
                  file={FILE_TYPE.TYPE_147}
                  hideRemarks
                  compact
                />
              </FormControl>
            )}
          </SimpleGrid>
        </Box>

        <LienHolder vehicle={vehicle} index={index} />

        <Lessor vehicle={vehicle} index={index} />

        <RatingInformation index={index} />

        <Coverages index={index} vehicle={vehicle} />
      </Collapse>
    </Box>
  );
};

export default Vehicle;
